import { Check, Pencil, UserRoundPlus } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Fragment, type ReactNode, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@apps/web/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { toast } from '@apps/web/lib/app_notifications';
import { formatChecklistUser } from '@apps/web/lib/checklist_user_options';
import { EVENTS } from '@apps/web/lib/events';
import { cn } from '@apps/web/lib/utils';
import { updateActivityEventAssignments } from '@apps/web/swr_data_fetching/activity_event_assignments';
import {
    type ActivityEvent,
    type ActivityEventAssignee,
    type ActivityEventUser,
} from '@libraries/typescript/api_client/schema_types';

export type ActivityAssigneeOption = {
    readonly id: string;
    readonly label: string;
    readonly name: string;
    readonly organizationIds: ReadonlySet<string>;
};

export type ActivityAssigneeScope =
    | { readonly kind: 'unassigned' }
    | { readonly kind: 'user'; readonly userId: string };

export function formatActivityUserName(user: {
    readonly id: string;
    readonly first_name?: string | null;
    readonly last_name?: string | null;
}): string {
    const name = [user.first_name, user.last_name]
        .filter((part) => Boolean(part))
        .join(' ')
        .trim();
    if (name) {
        return name;
    }
    return `Unnamed user · ${user.id.slice(-6)}`;
}

export function buildActivityAssigneeOptions(
    memberships: ReadonlyArray<ActivityEventAssignee>,
): ReadonlyArray<ActivityAssigneeOption> {
    const membershipsByUserId = new Map<
        string,
        {
            user: ActivityEventAssignee;
            organizationIds: Set<string>;
        }
    >();
    for (const membership of memberships) {
        const existing = membershipsByUserId.get(membership.id);
        if (existing) {
            existing.organizationIds.add(membership.organization_id);
        } else {
            membershipsByUserId.set(membership.id, {
                user: membership,
                organizationIds: new Set([membership.organization_id]),
            });
        }
    }
    return Array.from(membershipsByUserId.values())
        .map(({ user, organizationIds }) => ({
            id: user.id,
            label: formatChecklistUser(user),
            name: formatActivityUserName(user),
            organizationIds,
        }))
        .sort((left, right) => left.label.localeCompare(right.label));
}

export function activityAssigneeOptionsForOrganizations(
    options: ReadonlyArray<ActivityAssigneeOption>,
    organizationIds: ReadonlyArray<string>,
): ReadonlyArray<ActivityAssigneeOption> {
    const uniqueOrganizationIds = new Set(organizationIds);
    return options.filter((option) =>
        Array.from(uniqueOrganizationIds).every((organizationId) =>
            option.organizationIds.has(organizationId),
        ),
    );
}

export function activityAssigneeOptionName(
    option: ActivityAssigneeOption,
): string {
    return option.name;
}

function activityAssigneeInitials(displayName: string, userId: string): string {
    if (displayName.startsWith('Unnamed user · ')) {
        return userId.slice(-2).toUpperCase() || '?';
    }
    const parts = displayName.split(/\s+/).filter((part) => part.length > 0);
    if (parts.length === 0) {
        return '?';
    }
    return parts
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
}

const MAX_VISIBLE_ASSIGNEE_AVATARS = 3;

const ACTIVITY_ASSIGNEE_AVATAR_COLORS = [
    'bg-blue-100 text-blue-700 dark:bg-blue-500/25 dark:text-blue-200',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/25 dark:text-emerald-200',
    'bg-amber-100 text-amber-800 dark:bg-amber-500/25 dark:text-amber-200',
    'bg-violet-100 text-violet-700 dark:bg-violet-500/25 dark:text-violet-200',
    'bg-rose-100 text-rose-700 dark:bg-rose-500/25 dark:text-rose-200',
    'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/25 dark:text-cyan-200',
    'bg-orange-100 text-orange-700 dark:bg-orange-500/25 dark:text-orange-200',
    'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/25 dark:text-fuchsia-200',
    'bg-teal-100 text-teal-700 dark:bg-teal-500/25 dark:text-teal-200',
    'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/25 dark:text-indigo-200',
] as const;

function activityAssigneeAvatarColor(key: string): string {
    let hash = 0;
    for (let index = 0; index < key.length; index += 1) {
        hash = (hash * 31 + key.charCodeAt(index)) | 0;
    }
    const paletteIndex =
        Math.abs(hash) % ACTIVITY_ASSIGNEE_AVATAR_COLORS.length;
    return (
        ACTIVITY_ASSIGNEE_AVATAR_COLORS[paletteIndex] ??
        ACTIVITY_ASSIGNEE_AVATAR_COLORS[0]
    );
}

// Overlapping circles of user initials, capped at
// MAX_VISIBLE_ASSIGNEE_AVATARS with a "+N" chip for the rest. The caller
// supplies the wrapping element (tooltip trigger, hover-fade span, …).
function AssigneeAvatarCircles({
    users,
}: {
    readonly users: ReadonlyArray<ActivityEventAssignee | ActivityEventUser>;
}) {
    const visibleUsers = users.slice(0, MAX_VISIBLE_ASSIGNEE_AVATARS);
    const overflowCount = users.length - visibleUsers.length;
    return (
        <Fragment>
            {visibleUsers.map((user) => (
                <span
                    key={user.id}
                    className={cn(
                        'ring-background flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ring-1',
                        activityAssigneeAvatarColor(user.id),
                    )}
                >
                    {activityAssigneeInitials(
                        formatActivityUserName(user),
                        user.id,
                    )}
                </span>
            ))}
            {overflowCount > 0 ? (
                <span className="bg-muted text-muted-foreground ring-background flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ring-1">
                    +{overflowCount}
                </span>
            ) : null}
        </Fragment>
    );
}

/**
 * Read-only stack of assignee initials, shared by board cards and rows so
 * "who owns this" reads identically everywhere. The tooltip carries the
 * full names; `label` prefixes them (e.g. "Assigned to" / "Resolved by").
 */
export function ActivityUserAvatarStack({
    users,
    label,
    className,
}: {
    readonly users: ReadonlyArray<ActivityEventAssignee | ActivityEventUser>;
    readonly label: string;
    readonly className?: string;
}) {
    if (users.length === 0) {
        return null;
    }
    const names = users.map((user) => formatActivityUserName(user));
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <span
                    className={cn(
                        'flex shrink-0 items-center -space-x-1.5',
                        className,
                    )}
                    aria-label={`${label} ${names.join(', ')}`}
                >
                    <AssigneeAvatarCircles users={users} />
                </span>
            </TooltipTrigger>
            <TooltipContent side="top">
                {label} {names.join(', ')}
            </TooltipContent>
        </Tooltip>
    );
}

// The searchable people list both assignment popovers share: an optional
// "Unassign all" group on top, then every option. `selectedUserIds` marks
// options with a checked state (toggle semantics); null renders a plain
// pick list (replace semantics).
function AssigneeCommandMenu({
    assigneeOptions,
    updating,
    showUnassign,
    selectedUserIds,
    onUnassign,
    onSelectOption,
}: {
    readonly assigneeOptions: ReadonlyArray<ActivityAssigneeOption>;
    readonly updating: boolean;
    readonly showUnassign: boolean;
    readonly selectedUserIds: ReadonlyArray<string> | null;
    readonly onUnassign: () => void;
    readonly onSelectOption: (userId: string) => void;
}) {
    return (
        <Command>
            <CommandInput placeholder="Assign to…" className="h-9" />
            <CommandList className="max-h-48">
                <CommandEmpty>No people found.</CommandEmpty>
                {showUnassign ? (
                    <Fragment>
                        <CommandGroup>
                            <CommandItem
                                value="unassign"
                                disabled={updating}
                                onSelect={onUnassign}
                            >
                                Unassign all
                            </CommandItem>
                        </CommandGroup>
                        <CommandSeparator />
                    </Fragment>
                ) : null}
                <CommandGroup>
                    {assigneeOptions.map((option) => (
                        <CommandItem
                            key={option.id}
                            value={option.label}
                            disabled={updating}
                            onSelect={() => onSelectOption(option.id)}
                        >
                            {selectedUserIds !== null ? (
                                <Checkbox
                                    checked={selectedUserIds.includes(
                                        option.id,
                                    )}
                                    tabIndex={-1}
                                    aria-hidden={true}
                                    className="pointer-events-none"
                                />
                            ) : null}
                            <span className="truncate">
                                {activityAssigneeOptionName(option)}
                            </span>
                        </CommandItem>
                    ))}
                </CommandGroup>
            </CommandList>
        </Command>
    );
}

function ActivityAssigneePicker({
    organizationId,
    event,
    assigneeOptions,
    onChanged,
    tooltip,
    children,
}: {
    readonly organizationId: string;
    readonly event: ActivityEvent;
    readonly assigneeOptions: ReadonlyArray<ActivityAssigneeOption>;
    readonly onChanged: () => Promise<unknown> | void;
    readonly tooltip: string;
    readonly children: ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const [updating, setUpdating] = useState(false);
    const posthog = usePostHog();
    const assignedToIds = event.assigned_to_users.map(
        (assignee) => assignee.id,
    );

    async function applyAssignment(
        userIds: ReadonlyArray<string>,
        toggledUserId: string | null,
    ) {
        setUpdating(true);
        try {
            await updateActivityEventAssignments(
                organizationId,
                [event.id],
                userIds,
            );
            try {
                posthog?.capture(EVENTS.ACTIVITY_FEED_ASSIGN, {
                    organization_id: organizationId,
                    activity_event_id: event.id,
                    activity_event_kind: event.kind,
                    action: userIds.length === 0 ? 'unassign' : 'assign',
                    assigned_to_user_id: toggledUserId,
                    assigned_to_user_ids: [...userIds],
                    event_count: 1,
                    bulk: false,
                });
            } catch {
                // Analytics should never block the core assignment action.
            }
            if (userIds.length === 0) {
                toast.success('Assignment removed', {
                    persistNotification: false,
                });
                setOpen(false);
            } else {
                const option = assigneeOptions.find(
                    (candidate) => candidate.id === toggledUserId,
                );
                const added =
                    toggledUserId !== null && userIds.includes(toggledUserId);
                toast.success(
                    option
                        ? added
                            ? `Assigned to ${activityAssigneeOptionName(option)}`
                            : `Removed ${activityAssigneeOptionName(option)}`
                        : 'Assignment updated',
                    { persistNotification: false },
                );
            }
            await onChanged();
        } catch {
            toast.error('Failed to update assignment', {
                persistNotification: false,
            });
        } finally {
            setUpdating(false);
        }
    }

    function toggleAssignee(userId: string) {
        const nextIds = assignedToIds.includes(userId)
            ? assignedToIds.filter((assigneeId) => assigneeId !== userId)
            : [...assignedToIds, userId];
        void applyAssignment(nextIds, userId);
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <PopoverTrigger asChild={true}>{children}</PopoverTrigger>
                </TooltipTrigger>
                {open ? null : (
                    <TooltipContent side="left">{tooltip}</TooltipContent>
                )}
            </Tooltip>
            <PopoverContent
                align="end"
                className="w-64 p-0"
                data-row-click-ignore=""
                onClick={(clickEvent) => clickEvent.stopPropagation()}
            >
                <AssigneeCommandMenu
                    assigneeOptions={assigneeOptions}
                    updating={updating}
                    showUnassign={assignedToIds.length > 0}
                    selectedUserIds={assignedToIds}
                    onUnassign={() => void applyAssignment([], null)}
                    onSelectOption={toggleAssignee}
                />
            </PopoverContent>
        </Popover>
    );
}

/**
 * Bulk assignment menu for a multi-card selection: picking a person
 * assigns every selected item to exactly that person (replacing existing
 * assignees), so assign and reassign are the same gesture. "Unassign all"
 * clears every selected item.
 */
export function ActivityBulkAssignMenu({
    organizationId,
    events,
    assigneeOptions,
    onChanged,
    children,
}: {
    readonly organizationId: string;
    readonly events: ReadonlyArray<ActivityEvent>;
    readonly assigneeOptions: ReadonlyArray<ActivityAssigneeOption>;
    readonly onChanged: () => Promise<unknown> | void;
    readonly children: ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const [updating, setUpdating] = useState(false);
    const posthog = usePostHog();

    async function applyBulkAssignment(userIds: ReadonlyArray<string>) {
        setUpdating(true);
        try {
            await updateActivityEventAssignments(
                organizationId,
                events.map((event) => event.id),
                userIds,
            );
            try {
                posthog?.capture(EVENTS.ACTIVITY_FEED_ASSIGN, {
                    organization_id: organizationId,
                    action: userIds.length === 0 ? 'unassign' : 'assign',
                    assigned_to_user_ids: [...userIds],
                    event_count: events.length,
                    bulk: true,
                });
            } catch {
                // Analytics should never block the core assignment action.
            }
            const option = assigneeOptions.find(
                (candidate) => candidate.id === userIds[0],
            );
            toast.success(
                userIds.length === 0
                    ? `Removed assignment from ${events.length} item${events.length === 1 ? '' : 's'}`
                    : `Assigned ${events.length} item${events.length === 1 ? '' : 's'} to ${
                          option ? activityAssigneeOptionName(option) : 'user'
                      }`,
                { persistNotification: false },
            );
            await onChanged();
            setOpen(false);
        } catch {
            toast.error('Failed to update assignment', {
                persistNotification: false,
            });
        } finally {
            setUpdating(false);
        }
    }

    const anyAssigned = events.some(
        (event) => event.assigned_to_users.length > 0,
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild={true}>{children}</PopoverTrigger>
            <PopoverContent
                align="center"
                side="top"
                className="w-64 p-0"
                onClick={(clickEvent) => clickEvent.stopPropagation()}
            >
                <AssigneeCommandMenu
                    assigneeOptions={assigneeOptions}
                    updating={updating}
                    showUnassign={anyAssigned}
                    selectedUserIds={null}
                    onUnassign={() => void applyBulkAssignment([])}
                    onSelectOption={(userId) =>
                        void applyBulkAssignment([userId])
                    }
                />
            </PopoverContent>
        </Popover>
    );
}

function activityAssigneeTriggerState(event: ActivityEvent): {
    readonly assignedTo: ActivityEvent['assigned_to_users'];
    readonly tooltip: string;
} {
    const assignedTo = event.assigned_to_users;
    const assignedNames = assignedTo.map((assignee) =>
        formatActivityUserName(assignee),
    );
    const assignedBy = event.assigned_by_user
        ? formatActivityUserName(event.assigned_by_user)
        : null;
    return {
        assignedTo,
        tooltip:
            assignedNames.length > 0
                ? `Assigned to ${assignedNames.join(', ')}${assignedBy ? ` by ${assignedBy}` : ''} · Click to reassign`
                : 'Assign',
    };
}

export function ActivityAssigneeRowControl({
    organizationId,
    event,
    assigneeOptions,
    onChanged,
}: {
    readonly organizationId: string;
    readonly event: ActivityEvent;
    readonly assigneeOptions: ReadonlyArray<ActivityAssigneeOption>;
    readonly onChanged: () => Promise<unknown> | void;
}) {
    const { assignedTo, tooltip } = activityAssigneeTriggerState(event);
    if (assigneeOptions.length === 0 && assignedTo.length === 0) {
        return null;
    }
    return (
        <ActivityAssigneePicker
            organizationId={organizationId}
            event={event}
            assigneeOptions={assigneeOptions}
            onChanged={onChanged}
            tooltip={tooltip}
        >
            <button
                type="button"
                data-row-click-ignore=""
                aria-label={tooltip}
                onClick={(clickEvent) => clickEvent.stopPropagation()}
                className="group/assign hover:bg-muted focus-visible:ring-ring relative flex h-6 min-w-6 items-center justify-center rounded-md px-0.5 outline-none focus-visible:ring-2"
            >
                {assignedTo.length > 0 ? (
                    <Fragment>
                        <span className="flex items-center -space-x-1.5 transition-opacity group-hover/assign:opacity-0">
                            <AssigneeAvatarCircles users={assignedTo} />
                        </span>
                        <Pencil className="text-muted-foreground absolute h-3 w-3 opacity-0 transition-opacity group-hover/assign:opacity-100" />
                    </Fragment>
                ) : (
                    <UserRoundPlus className="text-muted-foreground h-3.5 w-3.5" />
                )}
            </button>
        </ActivityAssigneePicker>
    );
}

export function ActivityAssigneeTextControl({
    organizationId,
    event,
    assigneeOptions,
    onChanged,
    label,
    showAssignedAvatar = false,
    showUnassignedIcon = false,
}: {
    readonly organizationId: string;
    readonly event: ActivityEvent;
    readonly assigneeOptions: ReadonlyArray<ActivityAssigneeOption>;
    readonly onChanged: () => Promise<unknown> | void;
    readonly label: string;
    readonly showAssignedAvatar?: boolean;
    readonly showUnassignedIcon?: boolean;
}) {
    const { assignedTo, tooltip } = activityAssigneeTriggerState(event);
    const canAssign = assigneeOptions.length > 0 || assignedTo.length > 0;
    const className =
        'text-muted-foreground min-w-0 truncate text-left text-[12px] leading-4';
    const content =
        showAssignedAvatar && assignedTo.length > 0 ? (
            <AssigneeAvatarCircles users={assignedTo} />
        ) : showUnassignedIcon && label === 'Unassigned' ? (
            <UserRoundPlus className="h-3.5 w-3.5" />
        ) : (
            label
        );
    if (!canAssign) {
        return (
            <span
                aria-label={showUnassignedIcon ? tooltip : undefined}
                className={cn(
                    className,
                    showUnassignedIcon &&
                        label === 'Unassigned' &&
                        'inline-flex h-6 min-w-6 items-center justify-center',
                    showAssignedAvatar &&
                        assignedTo.length > 0 &&
                        'inline-flex h-6 min-w-6 items-center justify-center',
                )}
                role={showUnassignedIcon ? 'img' : undefined}
            >
                {content}
            </span>
        );
    }
    return (
        <ActivityAssigneePicker
            organizationId={organizationId}
            event={event}
            assigneeOptions={assigneeOptions}
            onChanged={onChanged}
            tooltip={tooltip}
        >
            <button
                type="button"
                data-row-click-ignore=""
                aria-label={tooltip}
                onClick={(clickEvent) => clickEvent.stopPropagation()}
                className={cn(
                    className,
                    'hover:text-foreground cursor-pointer hover:underline',
                    showUnassignedIcon &&
                        label === 'Unassigned' &&
                        'inline-flex h-6 min-w-6 items-center justify-center',
                    showAssignedAvatar &&
                        assignedTo.length > 0 &&
                        'inline-flex h-6 min-w-6 items-center justify-center',
                )}
            >
                {content}
            </button>
        </ActivityAssigneePicker>
    );
}

const ASSIGNEE_SCOPE_ACTIVE_CLASS =
    'border-blue-200 bg-blue-50/50 text-blue-900 hover:bg-blue-50';
const ASSIGNEE_SCOPE_INACTIVE_CLASS =
    'border-transparent bg-muted text-foreground hover:bg-muted/80';

function activityAssigneeScopesEqual(
    left: ActivityAssigneeScope | null,
    right: ActivityAssigneeScope,
): boolean {
    if (left === null || left.kind !== right.kind) {
        return false;
    }
    if (left.kind === 'unassigned') {
        return true;
    }
    return right.kind === 'user' && left.userId === right.userId;
}

export function ActivityAssigneeScopeMenu({
    assigneeOptions,
    currentUserId,
    selectedScope,
    onScopeChange,
}: {
    readonly assigneeOptions: ReadonlyArray<ActivityAssigneeOption>;
    readonly currentUserId: string | null;
    readonly selectedScope: ActivityAssigneeScope | null;
    readonly onScopeChange: (scope: ActivityAssigneeScope | null) => void;
}) {
    const [open, setOpen] = useState(false);
    const hasScope = selectedScope !== null;
    const selectedUserId =
        selectedScope?.kind === 'user' ? selectedScope.userId : null;
    const selectedAssigneeOption = selectedUserId
        ? assigneeOptions.find((option) => option.id === selectedUserId)
        : undefined;
    const buttonLabel =
        selectedScope === null
            ? 'Assignee'
            : selectedScope.kind === 'unassigned'
              ? 'Unassigned'
              : selectedScope.userId === currentUserId
                ? 'Assigned to me'
                : selectedAssigneeOption
                  ? activityAssigneeOptionName(selectedAssigneeOption)
                  : 'Assignee';

    const selectScope = (scope: ActivityAssigneeScope) => {
        onScopeChange(
            activityAssigneeScopesEqual(selectedScope, scope) ? null : scope,
        );
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild={true}>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-pressed={hasScope}
                    className={cn(
                        'h-8 rounded-full border px-3 text-xs',
                        hasScope
                            ? ASSIGNEE_SCOPE_ACTIVE_CLASS
                            : ASSIGNEE_SCOPE_INACTIVE_CLASS,
                    )}
                >
                    <UserRoundPlus className="h-3.5 w-3.5" />
                    {buttonLabel}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-0">
                <Command>
                    <CommandInput
                        placeholder="Filter by assignee…"
                        className="h-9"
                    />
                    <CommandList>
                        <CommandEmpty>No people found.</CommandEmpty>
                        <CommandGroup>
                            {currentUserId ? (
                                <CommandItem
                                    value="assigned-to-me"
                                    onSelect={() =>
                                        selectScope({
                                            kind: 'user',
                                            userId: currentUserId,
                                        })
                                    }
                                >
                                    Assigned to me
                                    {selectedUserId === currentUserId ? (
                                        <Check className="ml-auto h-3.5 w-3.5" />
                                    ) : null}
                                </CommandItem>
                            ) : null}
                            <CommandItem
                                value="unassigned"
                                onSelect={() =>
                                    selectScope({ kind: 'unassigned' })
                                }
                            >
                                Unassigned
                                {selectedScope?.kind === 'unassigned' ? (
                                    <Check className="ml-auto h-3.5 w-3.5" />
                                ) : null}
                            </CommandItem>
                        </CommandGroup>
                        <CommandGroup>
                            {assigneeOptions
                                .filter((option) => option.id !== currentUserId)
                                .map((option) => (
                                    <CommandItem
                                        key={option.id}
                                        value={option.label}
                                        onSelect={() =>
                                            selectScope({
                                                kind: 'user',
                                                userId: option.id,
                                            })
                                        }
                                    >
                                        <span className="truncate">
                                            {activityAssigneeOptionName(option)}
                                        </span>
                                        {selectedUserId === option.id ? (
                                            <Check className="ml-auto h-3.5 w-3.5" />
                                        ) : null}
                                    </CommandItem>
                                ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
